import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type {
  KycActiveProcess,
  KycResponse,
  OptionalKycActiveProcess,
} from '@repo/api-kit/queries/due-diligence/kyc';

export type RefetchKyc = (
  options?: RefetchOptions
) => Promise<QueryObserverResult<KycResponse | OptionalKycActiveProcess | null, Error>>;

export interface KycProviderDetailsContextValue {
  /** The KYC provider to use, as resolved by the app (Fourthline / IDNow / unspecified). */
  provider: KycActiveProcess['provider'];
  /** Provider-specific details (token, redirectUrl, status, ...) for initializing the SDK. */
  providerDetails: KycActiveProcess['providerDetails'];
  /**
   * Refetches the KYC data from the source that produced the provider details
   * (register endpoint / fourthlineProcess).
   * Omitted in acceptance mode (consumers fall back to the acceptance `kycQueries.item()` refetch).
   */
  refetchProviderDetails?: RefetchKyc;
}

export const KycProviderDetailsContext = createContext<KycProviderDetailsContextValue | null>(null);

export function KycProviderDetailsProvider({
  children,
  provider,
  providerDetails,
  refetchProviderDetails,
}: PropsWithChildren<KycProviderDetailsContextValue>) {
  const value = useMemo<KycProviderDetailsContextValue>(
    () => ({ provider, providerDetails, refetchProviderDetails }),
    [provider, providerDetails, refetchProviderDetails]
  );

  return (
    <KycProviderDetailsContext.Provider value={value}>
      {children}
    </KycProviderDetailsContext.Provider>
  );
}

export function useKycProviderDetails(): KycProviderDetailsContextValue {
  const context = useContext(KycProviderDetailsContext);
  if (!context) {
    throw new Error('useKycProviderDetails must be used within a KycProviderDetailsProvider');
  }
  return context;
}
